import React, { useState, useEffect, useRef } from 'react';
import api from '../../api';
import '../utils/RightDetails.css';
import './PartnerManager.css';
import { FiPlus, FiEdit2, FiTrash2, FiMapPin } from 'react-icons/fi';

const PartnerDetails = ({ data, onClose, onSave, showToast, onAddUser, onEditUser, onDeleteUser }) => {
  const [formData, setFormData] = useState(data || {});
  const [partnerUsers, setPartnerUsers] = useState([]);
  const [qrCodes, setQrCodes] = useState([]);
  const [allQrCodes, setAllQrCodes] = useState([]);
  const [boxes, setBoxes] = useState([]);
  const [selectedQrId, setSelectedQrId] = useState('');
  const [showMap, setShowMap] = useState(false);
  const [boxSearchQuery, setBoxSearchQuery] = useState('');
  const [editingBox, setEditingBox] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingQr, setLoadingQr] = useState(false);
  const [loadingBoxes, setLoadingBoxes] = useState(false);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);

  useEffect(() => {
    if (data?.id) {
      setFormData(data);
      loadPartnerUsers(data.id);
      loadQrCodes(data.id);
      loadAllQrCodes();
      loadBoxes(data.id);
    }
  }, [data]);

  // Initialize Leaflet map when showMap is true
  useEffect(() => {
    if (showMap && boxes.length > 0 && mapRef.current && !mapInstanceRef.current) {
      const initializeMap = async () => {
        // Dynamically import Leaflet
        const L = await import('leaflet');
        await import('leaflet/dist/leaflet.css');

        // Calculate center based on all boxes
        const validBoxes = boxes.filter(box => box.latitude && box.longitude);
        if (validBoxes.length === 0) return;

        const avgLat = validBoxes.reduce((sum, box) => sum + parseFloat(box.latitude), 0) / validBoxes.length;
        const avgLng = validBoxes.reduce((sum, box) => sum + parseFloat(box.longitude), 0) / validBoxes.length;

        const map = L.map(mapRef.current).setView([avgLat, avgLng], 12);

        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '&copy; OpenStreetMap contributors',
        }).addTo(map);

        // Add markers for each box
        validBoxes.forEach(box => {
          const marker = L.marker([parseFloat(box.latitude), parseFloat(box.longitude)]).addTo(map);
          marker.bindPopup(`
            <strong>${box.unitCode}</strong><br/>
            ${box.locationName || 'No location name'}<br/>
            ${box.notes ? `<em>${box.notes}</em>` : ''}
          `);
        });

        mapInstanceRef.current = map;
      };

      initializeMap();
    }

    // Cleanup on unmount or when map is hidden
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [showMap, boxes]);

  const loadPartnerUsers = async (partnerId) => {
    try {
      setLoading(true);
      const response = await api.get(`/partner/admin/partners/${partnerId}/users`);
      setPartnerUsers(response.data);
    } catch (error) {
      console.error('Failed to load partner users:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadQrCodes = async (partnerId) => {
    try {
      setLoadingQr(true);
      const response = await api.get(`/partner/admin/partners/${partnerId}/qr-codes`);
      setQrCodes(response.data);
    } catch (error) {
      console.error('Failed to load QR codes:', error);
    } finally {
      setLoadingQr(false);
    }
  };

  const loadAllQrCodes = async () => {
    try {
      const response = await api.get('/partner/admin/qr-codes/all');
      setAllQrCodes(response.data);
    } catch (error) {
      console.error('Failed to load QR codes:', error);
    }
  };

  const loadBoxes = async (partnerId) => {
    try {
      setLoadingBoxes(true);
      const response = await api.get(`/partner/admin/partners/${partnerId}/boxes`);
      setBoxes(response.data);
    } catch (error) {
      console.error('Failed to load boxes:', error);
    } finally {
      setLoadingBoxes(false);
    }
  };

  const handleAssignQr = async () => {
    if (!selectedQrId || !data?.id) return;

    try {
      await api.put(`/partner/admin/qr-codes/${selectedQrId}/assign`, {
        partnerId: data.id
      });
      showToast?.('QR code assigned successfully', 'success');
      setSelectedQrId('');
      await loadQrCodes(data.id);
      await loadAllQrCodes();
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Failed to assign QR code';
      showToast?.(errorMsg, 'error');
    }
  };

  const handleUnassignQr = async (qrId) => {
    if (!window.confirm('Are you sure you want to unassign this QR code from this partner?')) {
      return;
    }

    try {
      await api.delete(`/partner/admin/qr-codes/${qrId}/unassign`);
      showToast?.('QR code unassigned successfully', 'success');
      await loadQrCodes(data.id);
      await loadAllQrCodes();
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Failed to unassign QR code';
      showToast?.(errorMsg, 'error');
    }
  };

  const handleDeleteBox = async (boxId) => {
    if (!window.confirm('Are you sure you want to delete this box? This action cannot be undone.')) {
      return;
    }

    try {
      await api.delete(`/partner/admin/boxes/${boxId}`);
      showToast?.('Box deleted successfully', 'success');
      await loadBoxes(data.id);
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Failed to delete box';
      showToast?.(errorMsg, 'error');
    }
  };

  const handleUpdateBox = async (box) => {
    try {
      await api.put(`/partner/admin/boxes/${box.id}`, {
        unitCode: box.unitCode,
        formId: box.formId,
        locationName: box.locationName,
        latitude: box.latitude,
        longitude: box.longitude,
        notes: box.notes
      });
      showToast?.('Box updated successfully', 'success');
      setEditingBox(null);
      await loadBoxes(data.id);
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Failed to update box';
      showToast?.(errorMsg, 'error');
    }
  };

  // Filter boxes based on search query
  const filteredBoxes = boxes.filter(box => 
    box.unitCode?.toLowerCase().includes(boxSearchQuery.toLowerCase()) ||
    box.locationName?.toLowerCase().includes(boxSearchQuery.toLowerCase()) ||
    box.formName?.toLowerCase().includes(boxSearchQuery.toLowerCase())
  );

  const handleSave = async () => {
    try {
      await api.put(`/partner/admin/partners/${formData.id}`, {
        name: formData.name,
        isActive: formData.isActive
      });
      showToast?.('Partner updated successfully', 'success');
      if (onSave) {
        await onSave(formData);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Failed to update partner';
      showToast?.(errorMsg, 'error');
    }
  };

  return (
    <div className="right-details-container">
      {/* Topbar with close button */}
      <div className="right-details-topbar">
        <div className="person-name">
          {formData.name}
          <span className={`status-badge ${formData.isActive ? 'active' : 'inactive'}`}>
            {formData.isActive ? 'Active' : 'Inactive'}
          </span>
        </div>
        <button className="close-button" onClick={onClose}>
          ×
        </button>
      </div>

      {/* Form content */}
      <form className="right-details-form" onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
        <div className="fields-section">
          {/* Partner Name */}
          <div className="right-details-field">
            <div className="field-label">
              <label>Partner Name</label>
            </div>
            <div className="field-value">
              <input
                type="text"
                value={formData.name || ''}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
          </div>

          {/* Active Status */}
          <div className="right-details-field">
            <div className="field-label">
              <label>Status</label>
            </div>
            <div className="field-value">
              <label>
                <input
                  type="checkbox"
                  checked={formData.isActive || false}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
                {' '}Active
              </label>
            </div>
          </div>

          {/* Stats Display */}
          <div className="right-details-field">
            <div className="field-label">Statistics</div>
            <div className="field-value" style={{ display: 'flex', gap: '16px' }}>
              <div>
                <strong style={{ fontSize: '18px', color: 'var(--primary-color)' }}>{formData.userCount || 0}</strong>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Users</div>
              </div>
              <div>
                <strong style={{ fontSize: '18px', color: 'var(--primary-color)' }}>{formData.formCount || 0}</strong>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Forms</div>
              </div>
              <div>
                <strong style={{ fontSize: '18px', color: 'var(--primary-color)' }}>{formData.boxCount || 0}</strong>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Boxes</div>
              </div>
            </div>
          </div>
        </div>

        {/* Users Section */}
        <div className="company-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <h3>Users ({partnerUsers.length})</h3>
            {onAddUser && (
              <button
                type="button"
                onClick={onAddUser}
                className="btn-icon"
                title="Add User"
              >
                <FiPlus size={16} />
              </button>
            )}
          </div>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-muted)' }}>Loading users...</div>
          ) : partnerUsers.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-muted)' }}>No users yet</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px' }}>
              {partnerUsers.map((user) => (
                <div key={user.id} style={{ 
                  padding: '8px', 
                  background: 'var(--hover-bg)', 
                  borderRadius: '4px',
                  fontSize: '13px',
                  position: 'relative'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px' }}>
                    <div style={{ fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', flex: 1 }}>
                      <span>{user.username}</span>
                      {user.role === 'admin' && (
                        <span style={{
                          background: 'var(--primary-color, #007bff)',
                          color: 'white',
                          fontSize: '10px',
                          padding: '2px 6px',
                          borderRadius: '10px',
                          textTransform: 'uppercase',
                          fontWeight: '600'
                        }}>
                          Admin
                        </span>
                      )}
                      <span className={`status-badge ${user.isActive ? 'active' : 'inactive'}`}>
                        {user.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      {onEditUser && (
                        <button
                          type="button"
                          onClick={() => onEditUser(user)}
                          className="btn-icon"
                          title="Edit User"
                          style={{ padding: '4px 6px' }}
                        >
                          <FiEdit2 size={14} />
                        </button>
                      )}
                      {onDeleteUser && (
                        <button
                          type="button"
                          onClick={() => onDeleteUser(user)}
                          className="btn-icon"
                          title="Delete User"
                          style={{ padding: '4px 6px', color: 'var(--danger-color, #dc3545)' }}
                        >
                          <FiTrash2 size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                  {user.fullName && <div style={{ color: 'var(--text-muted)' }}>{user.fullName}</div>}
                  {user.email && <div style={{ color: 'var(--text-muted)' }}>{user.email}</div>}
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>
                    {user.lastLogin 
                      ? `Last login: ${new Date(user.lastLogin).toLocaleDateString()}`
                      : 'Never logged in'}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* QR Codes Section */}
        <div className="company-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <h3>QR Codes ({qrCodes.length})</h3>
          </div>
          
          {/* Add QR Code Dropdown */}
          <div style={{ marginBottom: '16px', display: 'flex', gap: '8px', alignItems: 'center' }}>
            <select
              value={selectedQrId}
              onChange={(e) => setSelectedQrId(e.target.value)}
              style={{
                flex: 1,
                padding: '8px',
                border: '1px solid var(--border)',
                borderRadius: '4px',
                background: 'var(--input-bg)',
                color: 'var(--input-text)',
                fontSize: '13px'
              }}
            >
              <option value="">Select QR code to assign...</option>
              {/* Unassigned QR codes first */}
              {allQrCodes.filter(qr => !qr.partnerId).map((qr) => (
                <option key={qr.id} value={qr.id}>
                  {qr.orgName || qr.emailTo} (ID: {qr.id})
                </option>
              ))}
              {/* Separator if there are assigned QRs */}
              {allQrCodes.some(qr => qr.partnerId) && (
                <option disabled>────────────────────</option>
              )}
              {/* Already assigned QR codes (greyed out but selectable) */}
              {allQrCodes.filter(qr => qr.partnerId).map((qr) => (
                <option 
                  key={qr.id} 
                  value={qr.id}
                  style={{ 
                    color: '#999',
                    fontStyle: 'italic'
                  }}
                >
                  {qr.orgName || qr.emailTo} - Assigned to: {qr.partnerName}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={handleAssignQr}
              disabled={!selectedQrId}
              className="btn-icon"
              title="Assign QR Code"
              style={{ 
                padding: '8px 12px',
                opacity: !selectedQrId ? 0.5 : 1,
                cursor: !selectedQrId ? 'not-allowed' : 'pointer'
              }}
            >
              <FiPlus size={16} />
            </button>
          </div>

          {loadingQr ? (
            <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-muted)' }}>Loading QR codes...</div>
          ) : qrCodes.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-muted)' }}>No QR codes assigned yet</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px' }}>
              {qrCodes.map((qr) => (
                <div key={qr.id} style={{ 
                  padding: '8px', 
                  background: 'var(--hover-bg)', 
                  borderRadius: '4px',
                  fontSize: '13px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <div>
                    <div style={{ fontWeight: '600', marginBottom: '4px' }}>
                      {qr.orgName || 'Unnamed QR'}
                    </div>
                    <div style={{ color: 'var(--text-muted)', fontSize: '12px' }}>
                      Email: {qr.emailTo}
                      {qr.phone && ` • Phone: ${qr.phone}`}
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>
                      Created: {new Date(qr.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleUnassignQr(qr.id)}
                    className="btn-icon"
                    title="Unassign QR Code"
                    style={{ padding: '4px 6px', color: 'var(--danger-color, #dc3545)' }}
                  >
                    <FiTrash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Boxes & Map Section */}
        <div className="company-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h3>
              <FiMapPin style={{ marginRight: '8px', display: 'inline' }} />
              Boxes ({filteredBoxes.length}{boxes.length !== filteredBoxes.length ? ` / ${boxes.length}` : ''})
            </h3>
            {boxes.filter(b => b.latitude && b.longitude).length > 0 && (
              <button
                type="button"
                onClick={() => setShowMap(!showMap)}
                className="btn-icon"
                title={showMap ? "Hide Map" : "Show Map"}
                style={{ padding: '6px 12px', display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                <FiMapPin size={16} />
                {showMap ? 'Hide Map' : 'Show Map'}
              </button>
            )}
          </div>

          {/* Search Bar */}
          {boxes.length > 0 && (
            <div style={{ marginBottom: '16px' }}>
              <input
                type="text"
                placeholder="Search boxes by code, location, or form..."
                value={boxSearchQuery}
                onChange={(e) => setBoxSearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid var(--border)',
                  borderRadius: '4px',
                  background: 'var(--input-bg)',
                  color: 'var(--input-text)',
                  fontSize: '13px'
                }}
              />
            </div>
          )}

          {showMap && boxes.filter(b => b.latitude && b.longitude).length > 0 && (
            <div style={{ marginBottom: '16px' }}>
              <div
                ref={mapRef}
                style={{
                  height: '300px',
                  width: '100%',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  border: '1px solid var(--border)'
                }}
              />
            </div>
          )}

          {loadingBoxes ? (
            <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-muted)' }}>Loading boxes...</div>
          ) : boxes.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-muted)' }}>No boxes registered yet</div>
          ) : filteredBoxes.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-muted)' }}>No boxes match your search</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px' }}>
              {filteredBoxes.map((box) => (
                <div key={box.id} style={{ 
                  padding: '8px', 
                  background: 'var(--hover-bg)', 
                  borderRadius: '4px',
                  fontSize: '13px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start'
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '600', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span>{box.unitCode}</span>
                      {box.latitude && box.longitude && (
                        <a
                          href={`https://www.google.com/maps/search/?api=1&query=${box.latitude},${box.longitude}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: 'var(--primary-color)', display: 'inline-flex', alignItems: 'center' }}
                          title="View on Google Maps"
                        >
                          <FiMapPin size={14} />
                        </a>
                      )}
                    </div>
                    {box.locationName && <div style={{ color: 'var(--text-muted)', fontSize: '12px' }}>{box.locationName}</div>}
                    {box.formName && <div style={{ color: 'var(--text-muted)', fontSize: '12px' }}>Form: {box.formName}</div>}
                    {box.notes && <div style={{ color: 'var(--text-muted)', fontSize: '11px', marginTop: '4px', fontStyle: 'italic' }}>{box.notes}</div>}
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>
                      Registered: {new Date(box.registeredAt).toLocaleDateString()}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <button
                      type="button"
                      onClick={() => setEditingBox(box)}
                      className="btn-icon"
                      title="Edit Box"
                      style={{ padding: '4px 6px' }}
                    >
                      <FiEdit2 size={14} />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeleteBox(box.id)}
                      className="btn-icon"
                      title="Delete Box"
                      style={{ padding: '4px 6px', color: 'var(--danger-color, #dc3545)' }}
                    >
                      <FiTrash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="right-details-actions">
          <button type="button" className="cancel-btn" onClick={onClose}>Cancel</button>
          <button type="submit" className="save-btn">Save Changes</button>
        </div>
      </form>

      {/* Edit Box Modal */}
      {editingBox && (
        <div className="modal-overlay" style={{ zIndex: 2000 }} onClick={() => setEditingBox(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Edit Box</h2>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '500' }}>
                Unit Code
              </label>
              <input
                type="text"
                value={editingBox.unitCode}
                onChange={(e) => setEditingBox({ ...editingBox, unitCode: e.target.value })}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid var(--border)',
                  borderRadius: '4px',
                  background: 'var(--input-bg)',
                  color: 'var(--input-text)',
                  fontSize: '13px'
                }}
              />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '500' }}>
                Location Name
              </label>
              <input
                type="text"
                value={editingBox.locationName || ''}
                onChange={(e) => setEditingBox({ ...editingBox, locationName: e.target.value })}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid var(--border)',
                  borderRadius: '4px',
                  background: 'var(--input-bg)',
                  color: 'var(--input-text)',
                  fontSize: '13px'
                }}
              />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '500' }}>
                Notes
              </label>
              <textarea
                value={editingBox.notes || ''}
                onChange={(e) => setEditingBox({ ...editingBox, notes: e.target.value })}
                rows={3}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid var(--border)',
                  borderRadius: '4px',
                  background: 'var(--input-bg)',
                  color: 'var(--input-text)',
                  fontSize: '13px',
                  resize: 'vertical'
                }}
              />
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setEditingBox(null)}
                style={{
                  padding: '8px 16px',
                  border: '1px solid var(--border)',
                  borderRadius: '4px',
                  background: 'transparent',
                  color: 'var(--text)',
                  cursor: 'pointer',
                  fontSize: '13px'
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleUpdateBox(editingBox)}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  borderRadius: '4px',
                  background: 'var(--primary-color)',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '13px'
                }}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartnerDetails;

